import React, { useEffect, useState } from "react";
import { totpCode, totpRemaining, parseTotpInput, copyPlain } from "@/lib/vaultCrypto";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

/** Viser gjeldende TOTP-kode med nedtellingsring (SVG). Klikk kopierer. */
export const TotpCode = ({ secret, compact = false, className, testId = "totp" }) => {
  const totp = parseTotpInput(secret);
  const [code, setCode] = useState("");
  const [remaining, setRemaining] = useState(30);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!totp) { setInvalid(true); return undefined; }
    setInvalid(false);
    let alive = true;
    let lastCounter = -1;
    const tick = async () => {
      const now = Date.now();
      const counter = Math.floor(now / 1000 / totp.period);
      setRemaining(totpRemaining(totp.period, now));
      if (counter !== lastCounter) {
        lastCounter = counter;
        try {
          const c = await totpCode(totp, now);
          if (alive) setCode(c);
        } catch {
          if (alive) setInvalid(true);
        }
      }
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => { alive = false; clearInterval(t); };
  }, [secret]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!totp || invalid) return <span className="text-xs text-destructive" data-testid={`${testId}-invalid`}>Ugyldig 2FA-nøkkel</span>;

  const period = totp.period || 30;
  const r = 9;
  const circ = 2 * Math.PI * r;
  const frac = remaining / period;
  const urgent = remaining <= 5;
  const pretty = code ? `${code.slice(0, Math.ceil(code.length / 2))} ${code.slice(Math.ceil(code.length / 2))}` : "••• •••";

  const copy = async (e) => {
    e?.stopPropagation?.();
    try { await copyPlain(code); toast.success("Engangskode kopiert", { id: "totp-copy" }); } catch { toast.error("Kunne ikke kopiere"); }
  };

  return (
    <button type="button" onClick={copy} className={cn("inline-flex items-center gap-2 rounded-full border bg-secondary/60 pl-1.5 pr-3 py-1 hover:bg-secondary transition-colors", className)} aria-label={`Engangskode ${code}, ${remaining} sekunder igjen. Klikk for å kopiere`} data-testid={`${testId}-code`}>
      <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true" className="-rotate-90">
        <circle cx="11" cy="11" r={r} fill="none" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2.5" />
        <circle cx="11" cy="11" r={r} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - frac)} className={cn("transition-[stroke-dashoffset] duration-1000 ease-linear", urgent ? "text-flag" : "text-primary")} />
      </svg>
      <span className={cn("font-mono tnum font-semibold tracking-widest", compact ? "text-sm" : "text-base")}>{pretty}</span>
      {!compact ? <span className={cn("text-xs tnum", urgent ? "text-flag" : "text-muted-foreground")}>{remaining}s</span> : null}
    </button>
  );
};
